/**
 * 系统配置相关API
 */

import { request } from '@/shared/api/request'
import type { AxiosRequestConfig } from 'axios'
import type { BatchRequestConfig } from '@/shared/api/use-api-batch'

// 系统配置相关接口

export interface SystemConfig {
  id: number
  config_key: string
  config_value: string
  config_type: 'string' | 'number' | 'boolean' | 'json'
  description?: string
  created_at: string
  updated_at: string
}

export interface SystemConfigBatchUpdate {
  configs: Record<string, string>
}

export interface HotWordTopConfig {
  hot_word_top_count: number
}

export interface ProjectRetentionConfig {
  retention_days: number
}

export interface SystemSettings {
  hot_word_top_count: number
  project_retention_days: number
  max_file_size: number
  supported_video_formats: string[]
  supported_document_formats: string[]
  supported_subtitle_formats: string[]
}

export interface PublicSystemSettings {
  max_file_size: number
  supported_video_formats: string[]
  supported_document_formats: string[]
  supported_subtitle_formats: string[]
  system_maintenance_mode: boolean
}

/**
 * 批量更新系统配置
 */
export const batchUpdateSystemConfigs = (data: SystemConfigBatchUpdate): Promise<SystemConfig[]> => {
  return request.post('/system-configs/batch', data)
}

/**
 * 获取系统设置概览
 */
export const getSystemSettingsOverview = (
  config?: AxiosRequestConfig | BatchRequestConfig,
): Promise<SystemSettings> => {
  return request.get('/system-configs/settings/overview', config)
}

/**
 * 获取热词TOP数量配置
 */
export const getHotWordTopCount = (): Promise<{ hot_word_top_count: number }> => {
  return request.get('/system-configs/hot-word-top/current')
}

/**
 * 更新热词TOP数量配置
 */
export const updateHotWordTopCount = (data: HotWordTopConfig): Promise<SystemConfig> => {
  return request.put('/system-configs/hot-word-top', data)
}

/**
 * 获取项目保留天数配置
 */
export const getProjectRetentionDays = (): Promise<{ project_retention_days: number }> => {
  return request.get('/system-configs/project-retention/current')
}

/**
 * 更新项目保留天数配置
 */
export const updateProjectRetentionDays = (data: ProjectRetentionConfig): Promise<SystemConfig> => {
  return request.put('/system-configs/project-retention', data)
}

/**
 * 初始化默认系统配置
 */
export const initializeDefaultConfigs = (): Promise<{ message: string }> => {
  return request.post('/system-configs/initialize')
}

/**
 * 获取公共系统设置（无需认证）
 */
export const getPublicSystemSettings = (): Promise<PublicSystemSettings> => {
  return request.get('/system-configs/public/settings')
}

/**
 * 获取系统状态信息
 */
export const getSystemStatus = (
  config?: AxiosRequestConfig | BatchRequestConfig,
): Promise<{
  maintenance_mode: boolean
  monitoring_enabled: boolean
  total_configs: number
  config_types: Record<string, number>
}> => {
  return request.get('/system-configs/system/status', config)
}

/**
 * 设置系统维护模式
 */
export const setMaintenanceMode = (enabled: boolean): Promise<{
  message: string
  config: SystemConfig
}> => {
  return request.put('/system-configs/system/maintenance-mode', null, {
    params: { enabled }
  })
}

/**
 * 获取清理相关设置
 */
export const getCleanupSettings = (): Promise<{
  project_retention_days: number
  cleanup_notification_days: number
  enable_auto_cleanup: boolean
  log_retention_days: number
}> => {
  return request.get('/system-configs/cleanup/settings')
}

/**
 * 更新清理相关设置
 */
export const updateCleanupSettings = (settings: Record<string, any>): Promise<{
  message: string
  updated_configs: SystemConfig[]
}> => {
  return request.put('/system-configs/cleanup/settings', settings)
}

/**
 * 获取翻译相关设置
 */
export const getTranslationSettings = (): Promise<{
  hot_word_top_count: number
  translation_timeout: number
  max_concurrent_translations: number
  max_subtitle_length: number
  enable_translation_cache: boolean
  translation_cache_ttl: number
  default_source_language: string
  default_target_language: string
}> => {
  return request.get('/system-configs/translation/settings')
}

/**
 * 更新翻译相关设置
 */
export const updateTranslationSettings = (settings: Record<string, any>): Promise<{
  message: string
  updated_configs: SystemConfig[]
}> => {
  return request.put('/system-configs/translation/settings', settings)
}

// 导出 API 对象（L3 端点；表 CRUD 已迁至 Collab L2 `/system/configs/*`）
export const systemConfigApi = {
  batchUpdateSystemConfigs,
  getSystemSettingsOverview,
  getHotWordTopCount,
  updateHotWordTopCount,
  getProjectRetentionDays,
  updateProjectRetentionDays,
  initializeDefaultConfigs,
  getPublicSystemSettings,
  getSystemStatus,
  setMaintenanceMode,
  getCleanupSettings,
  updateCleanupSettings,
  getTranslationSettings,
  updateTranslationSettings
}

